import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import toast from 'react-hot-toast';
import { resultsService } from '../../services/results.service';
import { pdfJobsService } from '../../services/pdfJobs.service';
import { buildCumulative, CumulativeData, CumulativeKey } from '../../lib/aptCumulative';
import { normalizeAptStats } from '../../lib/aptCompare';

interface AptCompareViewProps {
  isPdfMode?: boolean;
  initialCompany?: string;
}

// 항목별 색상 (대시보드와 동일 팔레트)
const ITEM_COLOR: Record<CumulativeKey, string> = {
  unread: 'rgba(156, 100, 200, 0.85)',
  emailOpened: 'rgba(255, 220, 100, 0.85)',
  linkClicked: 'rgba(255, 120, 120, 0.85)',
  infoInput: 'rgba(46, 204, 113, 0.85)',
  spamReport: 'rgba(120, 180, 240, 0.85)',
};
const ITEM_BORDER: Record<CumulativeKey, string> = {
  unread: 'rgba(156, 100, 200, 1)',
  emailOpened: 'rgba(255, 220, 100, 1)',
  linkClicked: 'rgba(255, 120, 120, 1)',
  infoInput: 'rgba(46, 204, 113, 1)',
  spamReport: 'rgba(120, 180, 240, 1)',
};

const AptCompareView: React.FC<AptCompareViewProps> = ({ isPdfMode = false, initialCompany }) => {
  const [company, setCompany] = useState<string>(initialCompany || '');
  const [companies, setCompanies] = useState<string[]>([]);
  const [data, setData] = useState<CumulativeData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // 회사 목록 로드 (PDF 모드에서는 드롭다운 불필요)
  useEffect(() => {
    if (isPdfMode) return;
    resultsService
      .getAvailableCompanies()
      .then(setCompanies)
      .catch((e) => console.error('Failed to load companies:', e));
  }, [isPdfMode]);

  // 전체 회차 × 선택 회사 → 회차별 누적 집계
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const rounds = await resultsService.getAvailableRounds(); // asc
      // 개별 회차 조회 실패가 전체 화면을 막지 않도록 allSettled — 실패 회차는 0으로 처리
      const settled = await Promise.allSettled(
        rounds.map((r) => resultsService.getStats({ round: r, company: company || undefined })),
      );
      const statsByRound: Record<string, any> = {};
      rounds.forEach((r, i) => {
        const res = settled[i];
        if (res.status === 'rejected') {
          console.error(`회차 ${r} 통계 조회 실패:`, res.reason);
        }
        statsByRound[r] = normalizeAptStats(res.status === 'fulfilled' ? res.value : {});
      });
      setData(buildCumulative(rounds, statsByRound));
    } catch (error) {
      console.error('Failed to load cumulative data:', error);
      setData({ rounds: [], series: [] });
    } finally {
      setIsLoading(false);
    }
  }, [company]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // PDF 모드: 데이터 로딩 완료 신호 (Puppeteer 대기용)
  useEffect(() => {
    if (!isPdfMode) return;
    (window as any).__pdfIsLoading = isLoading;
    (window as any).__pdfDataLoaded = !isLoading && data !== null;
  }, [isPdfMode, isLoading, data]);

  const handleDownloadPdf = async () => {
    const scope = company || '전체';
    const toastId = toast.loading('PDF 작업 시작 중...');
    setIsDownloadingPdf(true);
    try {
      const { jobId } = await pdfJobsService.createJob('apt', '', scope, { tab: 'compare' });
      toast.loading('PDF 생성 중... 0초 경과 (백그라운드 처리)', { id: toastId });
      const blob = await pdfJobsService.pollUntilDone(jobId, (elapsed) => {
        toast.loading(`PDF 생성 중... ${elapsed}초 경과 (백그라운드 처리)`, { id: toastId });
      });
      const fileName = `[${scope}] 악성메일 모의 훈련 회차별 누적.pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF 다운로드 완료', { id: toastId });
    } catch (error: any) {
      console.error('PDF download failed:', error);
      const msg = error.response?.data?.message || error.message || 'PDF 생성에 실패했습니다.';
      toast.error('PDF 생성 실패: ' + msg, { id: toastId });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // 모든 항목 차트가 동일 옵션을 공유 — 매 렌더 새 객체 생성 방지
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // PDF 모드: 막대 애니메이션 비활성화 — Puppeteer가 애니메이션 완료 전 캡처해
      // 일부 막대가 누락되는 문제 방지 (즉시 최종 상태로 렌더)
      animation: isPdfMode ? (false as const) : undefined,
      plugins: {
        legend: { display: false },
        title: { display: false },
        datalabels: {
          anchor: 'end' as const,
          align: 'top' as const,
          color: '#1e293b',
          font: { size: 11, weight: 'bold' as const },
          textStrokeColor: '#fff',
          textStrokeWidth: 2,
          formatter: (v: number) => (v > 0 ? v : ''),
        },
      },
      // 막대 위 숫자(datalabels)가 차트 상단에서 잘리지 않도록 여유 확보
      layout: { padding: { top: 28 } },
      scales: { y: { beginAtZero: true, grace: '10%' } },
    }),
    [isPdfMode],
  );

  return (
    <div className="apt-compare">
      <div className="apt-compare-toolbar">
        {!isPdfMode && (
          <div className="filter-group">
            <label htmlFor="compare-company">회사</label>
            <select
              id="compare-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            >
              <option value="">전체</option>
              {companies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="apt-compare-scope">
          {company || '전체'} · 회차별 누적
        </div>
        {!isPdfMode && (
          <button
            className="export-button"
            onClick={handleDownloadPdf}
            disabled={isLoading || isDownloadingPdf || !data || data.rounds.length === 0}
          >
            {isDownloadingPdf ? 'PDF 생성 중...' : 'PDF 다운로드'}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="loading">데이터를 불러오는 중...</div>
      ) : !data || data.rounds.length === 0 ? (
        <div className="no-data">누적할 회차 데이터가 없습니다.</div>
      ) : (
        data.series.map((s) => {
          const chartData = {
            labels: data.rounds,
            datasets: [
              {
                label: s.label,
                data: s.values,
                backgroundColor: ITEM_COLOR[s.key],
                borderColor: ITEM_BORDER[s.key],
                borderWidth: 1,
              },
            ],
          };
          return (
            <div className="apt-compare-item" key={s.key}>
              <div className="apt-compare-item-title">
                <span
                  className="apt-compare-dot"
                  style={{ background: ITEM_COLOR[s.key], borderColor: ITEM_BORDER[s.key] }}
                />
                {s.label}
              </div>
              <div className="apt-compare-chart">
                <Bar data={chartData} options={chartOptions} plugins={[ChartDataLabels]} />
              </div>
              {/* 각 차트마다 해당 항목의 회차별 표 */}
              <div className="themed-table-wrap">
                <table className="themed-table apt-compare-table">
                  <thead>
                    <tr>
                      <th>회차</th>
                      {data.rounds.map((r) => (
                        <th key={r}>{r}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{s.label}</td>
                      {s.values.map((v, i) => (
                        <td key={i}>{v}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default AptCompareView;
